import { PricePrecedence } from '../config/finance/financeConfig'
import { PriceVat } from '../components/Form/Finance/Invoice/InvoiceForm'
import { parseNumber } from './numberHelper'
import { Currency, VatType } from '../config/i18nConfig'

export const getPriceVat = (
    amount: number,
    vatType: VatType,
    currency: Currency,
    precedence?: PricePrecedence.Subtotal | PricePrecedence.Total,
): PriceVat => {
    let subtotal: number = 0
    let vat: number = 0
    let total: number = 0

    if (amount && vatType) {
        switch (precedence || PricePrecedence.Subtotal) {
            case PricePrecedence.Subtotal:
                subtotal = parseNumber(amount, 'en')
                vat = parseNumber((amount * vatType.percentage) / 100, 'en')
                total = parseNumber(subtotal + vat, 'en')
                break
            case PricePrecedence.Total:
                total = parseNumber(amount, 'en')
                vat = parseNumber((amount * vatType.percentage) / (100 + vatType.percentage), 'en')
                subtotal = parseNumber(total - vat, 'en')
                break
        }
    }

    return {
        subtotal: roundPrice(subtotal, currency.decimals),
        vat: vatType && [
            {
                type: vatType.id,
                value: roundPrice(vat, currency.decimals),
            },
        ],
        total: roundPrice(total, currency.decimals),
    } as PriceVat
}

export const reducePrices = (acc: PriceVat, price: PriceVat): PriceVat => ({
    subtotal: price.subtotal + acc.subtotal,
    vat: (price.vat || []).reduce<PriceVat['vat']>((acc: PriceVat['vat'], vat: { value: number; type: string }) => {
        const match = acc.find(_vat => _vat.type === vat.type)
        if (!match) {
            acc.push({ ...vat })
        } else {
            match.value += vat.value
        }
        return acc
    }, acc.vat),
    total: price.total + acc.total,
})

const VatAmountCallbackMap = new Map<PricePrecedence, (amount: number, vatType: VatType) => number>([
    [PricePrecedence.Subtotal, (amount, vatType) => (amount * vatType.percentage) / 100],
    [PricePrecedence.Total, (amount, vatType) => (amount * vatType.percentage) / (100 + vatType.percentage)],
])

const PriceAmountCallback = new Map<PricePrecedence, (amount: number, vatType: VatType) => number>([
    [PricePrecedence.Subtotal, (amount, vatType) => amount + VatAmountCallbackMap.get(PricePrecedence.Subtotal)(amount, vatType)],
    [PricePrecedence.Total, (amount, vatType) => amount - VatAmountCallbackMap.get(PricePrecedence.Total)(amount, vatType)],
])

export const formatPriceVat = (
    amount: number,
    vatType: VatType,
    currency: Currency,
    precedence: PricePrecedence = PricePrecedence.Subtotal,
): PriceVat => {
    const roundedAmount = amount && roundPrice(amount, currency.decimals)
    const other = vatType && PriceAmountCallback.get(precedence)(amount, vatType)
    const roundedOther = other && roundPrice(other, currency.decimals)
    const vat = vatType && VatAmountCallbackMap.get(precedence)(amount, vatType)
    const roundedVat = vat && roundPrice(vat, currency.decimals)

    return {
        subtotal: precedence === PricePrecedence.Subtotal ? roundedAmount : roundedOther,
        vat: [
            {
                type: vatType?.id,
                value: roundedVat,
            },
        ],
        total: precedence === PricePrecedence.Total ? roundedAmount : roundedOther,
    } as PriceVat
}

const roundPrice = (num, decimalPlaces = 0): number => {
    if (num < 0) {
        return -roundPrice(-num, decimalPlaces)
    }

    num = Math.round(Number(num + 'e' + decimalPlaces))
    return Number(num + 'e' + -decimalPlaces)
}
